import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { api } from '@carrent/api'

const LEVEL_STYLE = {
  error:   { bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.35)',   text: 'var(--danger)',  icon: '🔴' },
  warning: { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.35)',  text: 'var(--warning)', icon: '🟡' },
  info:    { bg: 'rgba(59,130,246,0.08)',  border: 'rgba(59,130,246,0.35)',  text: 'var(--accent)',  icon: '🔵' },
}

const DAYS_OPTIONS = [
  { value: 7,  label: '7 дней' },
  { value: 14, label: '14 дней' },
  { value: 30, label: '30 дней' },
  { value: 60, label: '60 дней' },
  { value: 90, label: '90 дней' },
]

function ReminderBadge({ level, text, icon, label }) {
  const s = LEVEL_STYLE[level] || LEVEL_STYLE.info
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '7px 10px',
      background: s.bg, borderRadius: 'var(--r-sm)',
      border: `1px solid ${s.border}`,
    }}>
      <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '0.8rem', fontWeight: 600, color: s.text }}>{label}</p>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 1 }}>{text}</p>
      </div>
    </div>
  )
}

function CarReminderCard({ car, onClick }) {
  const s = LEVEL_STYLE[car.top_level] || LEVEL_STYLE.info
  return (
    <div style={{
      background: 'var(--bg-card)', borderRadius: 'var(--r)',
      border: `1px solid ${s.border}`, overflow: 'hidden',
      cursor: 'pointer',
    }} onClick={onClick}>
      {/* Шапка */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px var(--gap)',
        borderBottom: `1px solid ${s.border}`,
        background: s.bg,
      }}>
        <div>
          <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>
            {car.car_brand} {car.car_model}
          </p>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {car.car_mileage?.toLocaleString()} км
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{
            display: 'inline-block', background: '#fff', color: '#000',
            padding: '1px 8px', borderRadius: 4, fontSize: '0.72rem',
            fontWeight: 800, letterSpacing: '0.08em',
            border: '1px solid #333', fontFamily: 'monospace',
          }}>{car.car_plate}</span>
          <p style={{ fontSize: '0.7rem', color: s.text, fontWeight: 700, marginTop: 3 }}>
            {s.icon} {car.reminders.length} напом.
          </p>
        </div>
      </div>

      {/* Напоминания */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 'var(--gap-sm) var(--gap)' }}>
        {car.reminders.map((r, i) => (
          <ReminderBadge key={i} {...r} />
        ))}
      </div>
    </div>
  )
}

export default function AdminRemindersPage() {
  const navigate = useNavigate()
  const [data, setData]     = useState(null)
  const [loading, setLoading] = useState(true)
  const [days, setDays]     = useState(30)
  const [levelFilter, setLevel] = useState('all')

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/reminders', { params: { days, status: levelFilter } })
      .then(r => setData(r.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [days, levelFilter])

  useEffect(load, [load])

  const counts = data?.counts || {}

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader title="Напоминания" />
      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {/* Счётчики */}
        {data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--gap-sm)' }}>
            {[
              { key: 'error',   label: 'Просрочено', color: 'var(--danger)' },
              { key: 'warning', label: 'Скоро',      color: 'var(--warning)' },
              { key: 'info',    label: 'Заметки',    color: 'var(--accent)' },
            ].map(({ key, label, color }) => (
              <button key={key}
                onClick={() => setLevel(levelFilter === key ? 'all' : key)}
                style={{
                  background: levelFilter === key ? `${color}18` : 'var(--bg-card)',
                  border: `1px solid ${levelFilter === key ? color : 'var(--border)'}`,
                  borderRadius: 'var(--r)', padding: 'var(--gap-sm)',
                  cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 800, color: counts[key] > 0 ? color : 'var(--text-muted)' }}>
                  {counts[key] || 0}
                </p>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{label}</p>
              </button>
            ))}
          </div>
        )}

        {/* Фильтр горизонта */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {DAYS_OPTIONS.map(o => (
            <button key={o.value}
              className={`btn ${days === o.value ? 'btn--primary' : 'btn--outline'}`}
              style={{ flexShrink: 0, padding: '5px 12px', fontSize: '0.8rem' }}
              onClick={() => setDays(o.value)}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Список */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" />
          </div>
        ) : data?.items?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
            <p style={{ fontWeight: 600 }}>Всё в порядке</p>
            <p className="text-muted" style={{ fontSize: '0.88rem', marginTop: 6 }}>
              Нет напоминаний на ближайшие {days} дней
            </p>
          </div>
        ) : (
          <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
            {data?.items?.map(car => (
              <CarReminderCard key={car.car_id} car={car}
                onClick={() => navigate(`/admin/cars/${car.car_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
